import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { TERMS_AND_CONDITIONS, CONTACT_INFO } from '../config/content';
import SEO from '../components/SEO';
import styles from './PrivacyPolicy.module.css';

const Reveal: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-50px' });
  return (
    <motion.div
      ref={ref}
      className={className}
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, ease: 'easeOut' }}
    >
      {children}
    </motion.div>
  );
};

const renderParagraphs = (text: string) =>
  text.split('\n\n').map((para, i) => (
    <p key={i} className={styles.paragraph}>
      {para}
    </p>
  ));

const renderList = (items: string[]) => (
  <ul className={styles.rightsList}>
    {items.map((item, i) => (
      <li key={i}>{item}</li>
    ))}
  </ul>
);

const TermsAndConditions: React.FC = () => {
  return (
    <main className={styles.page}>
      <SEO path="/terms-and-conditions" />
      <div className={`${styles.ambient} ${styles.ambient1}`} />
      <div className={`${styles.ambient} ${styles.ambient2}`} />

      {/* Hero */}
      <div className={styles.heroWrapper}>
        <div className={styles.hero}>
          <motion.div
            className={styles.heroInner}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
          >
            <div className={styles.heroBadge}>
              <div className={styles.badgeDot} />
              <span>{TERMS_AND_CONDITIONS.hero.label}</span>
            </div>
            <h1>{TERMS_AND_CONDITIONS.hero.title}</h1>
            <p className={styles.heroSub}>{TERMS_AND_CONDITIONS.hero.subtitle}</p>
          </motion.div>
        </div>
        <div className={styles.heroFade} />
      </div>

      <div className={styles.sectionDivider} />

      {/* Content */}
      <section className={styles.contentSection}>
        <div className={styles.contentContainer}>
          <Reveal>
            <p className={styles.lastUpdated}>Last updated: {TERMS_AND_CONDITIONS.lastUpdated}</p>
          </Reveal>

          {TERMS_AND_CONDITIONS.sections.map((section, i) => (
            <Reveal key={i} className={styles.policyBlock}>
              <h2 className={styles.sectionTitle}>{section.title}</h2>
              {renderParagraphs(section.content)}
              {'list' in section && section.list && renderList(section.list)}
              {'afterList' in section && section.afterList && renderParagraphs(section.afterList)}
              {'secondList' in section && (section as any).secondList && renderList((section as any).secondList)}
              {'afterSecondList' in section && (section as any).afterSecondList && renderParagraphs((section as any).afterSecondList)}
              {'thirdList' in section && (section as any).thirdList && renderList((section as any).thirdList)}
              {'afterThirdList' in section && (section as any).afterThirdList && renderParagraphs((section as any).afterThirdList)}
            </Reveal>
          ))}

          <Reveal className={styles.contactBox}>
            <h3>Questions about these terms?</h3>
            <p>
              Reach out to us at{' '}
              <a href={CONTACT_INFO.emailHref}>{CONTACT_INFO.email}</a> or call{' '}
              <a href={CONTACT_INFO.phoneHref}>{CONTACT_INFO.phone}</a>.
            </p>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default TermsAndConditions;
